// Prix d'une course (transport, lot T2) dans la fenêtre de course. L'outil PROPOSE, n'impose jamais :
// - « Prix proposé » = dernier prix du même donneur d'ordre, même classe, même trajet (sinon trajet inverse ; mise à
//   disposition : dernier tarif horaire) avec sa provenance ; « Utiliser » le reprend comme prix de base ;
// - suppléments proposés (nuit OU dimanche / jour férié) cochés d'office, décochables et modifiables ; un montant modifié
//   à la main n'est jamais écrasé : la nouvelle proposition est seulement signalée ;
// - total TTC = base + suppléments cochés, toujours modifiable à la main (« Total modifié à la main… Recalculer ») ;
// - HT et TVA selon le régime de TVA (franchise : « TVA non applicable »).
// Le détail n'est envoyé au serveur que si l'on a touché au prix (`dirty`) : une ancienne course rouverte pour autre chose
// garde exactement ses données. Le serveur ne l'envoie jamais au chauffeur.
import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { money } from "@/lib/helpers";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles } from "lucide-react";

const num = (v) => {
  const n = parseFloat(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};
const r2 = (n) => Math.round((n + 1e-9) * 100) / 100;
const MAJORATIONS = ["night", "holiday"]; // les suppléments proposés par l'outil (l'attente s'ajoute à part, lot T2-B2)

const dateCourte = (iso, tz) => (iso ? new Intl.DateTimeFormat("fr-FR", { timeZone: tz, day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(iso)) : "");

export default function RidePrice({ draft, excludeId, price, details, onChange, cur, tz, disabled }) {
  const hourly = draft.ride_kind === "hourly";
  const hours = num(draft.duration_hours);
  // État de départ : le détail enregistré (suppléments déjà choisis = choix de l'utilisateur, jamais remplacés) ;
  // une ancienne course sans détail part de son prix total.
  const [base, setBase] = useState(() => String(details?.base ?? (hourly ? "" : (num(price) || ""))));
  const [rate, setRate] = useState(() => String(details?.hourly_rate ?? ""));
  const [supps, setSupps] = useState(() => (details?.supplements || []).map((s) => ({ ...s, on: true, touched: true })));
  const [source, setSource] = useState(details?.source || null);
  const [manual, setManual] = useState(() => {
    if (!details) return false;
    const calc = r2(num(details.base) + (details.supplements || []).reduce((t, s) => t + num(s.amount), 0));
    return r2(num(price)) !== calc;
  });
  const [total, setTotal] = useState(String(price ?? ""));
  const [proposal, setProposal] = useState(null);
  const [dirty, setDirty] = useState(false);
  const touch = () => setDirty(true);
  const nouvelle = !excludeId; // course en création : la majoration proposée est cochée d'office

  const baseValue = hourly ? r2(num(rate) * hours) : num(base);
  const calcule = r2(baseValue + supps.filter((s) => s.on).reduce((t, s) => t + num(s.amount), 0));

  // Proposition du serveur : à chaque changement du trajet, de l'heure, de la classe ou du prix de base (regroupé).
  const timer = useRef(null);
  const corps = JSON.stringify({
    client_id: draft.client_id, ride_kind: draft.ride_kind || "transfer", vehicle_class: draft.vehicle_class || "",
    location: draft.location || "", pickup_place_id: draft.pickup_place_id || "", dropoff_location: draft.dropoff_location || "",
    dropoff_place_id: draft.dropoff_place_id || "", start: draft.start ? new Date(draft.start).toISOString() : "",
    end: draft.end ? new Date(draft.end).toISOString() : "", duration_hours: hourly ? hours : null, exclude_id: excludeId || "",
    base: !hourly && String(base).trim() !== "" ? num(base) : undefined,
    hourly_rate: hourly && String(rate).trim() !== "" ? num(rate) : undefined,
  });
  useEffect(() => {
    const b = JSON.parse(corps);
    if (!b.client_id || !b.start) { setProposal(null); return undefined; }
    let stale = false;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      api.post("/rides/price-proposal", b).then((r) => { if (!stale) setProposal(r.data); }).catch(() => { if (!stale) setProposal(null); });
    }, 350);
    return () => { stale = true; clearTimeout(timer.current); };
  }, [corps]);

  // Majoration proposée : remplace la majoration proposée non touchée ; une majoration touchée (décochée ou montant
  // changé) n'est jamais remplacée sans le dire.
  useEffect(() => {
    if (!proposal || (!nouvelle && !dirty)) return; // course existante : seulement proposée (« Ajouter »), jamais ajoutée seule
    const propose = proposal.supplements[0];
    setSupps((list) => {
      if (list.some((s) => MAJORATIONS.includes(s.key) && s.touched)) return list;
      const autres = list.filter((s) => !MAJORATIONS.includes(s.key));
      return propose ? [...autres, { ...propose, on: true, touched: false }] : autres;
    });
  }, [proposal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Le total suit le calcul dès qu'on a touché au prix, sauf s'il a été tapé à la main ; sinon il reste le prix enregistré.
  useEffect(() => { if (dirty && !manual) setTotal(String(calcule)); }, [dirty, calcule, manual]);
  useEffect(() => {
    if (!dirty) { onChange({ price: total, details: undefined }); return; }
    const d = { mode: hourly ? "hourly" : "transfer", base: baseValue,
      supplements: supps.filter((s) => s.on).map(({ key, label, amount, pct }) => ({ key, label, amount: num(amount), ...(pct != null ? { pct } : {}) })),
      ...(hourly ? { hourly_rate: num(rate), hours } : {}), ...(source ? { source } : {}) };
    onChange({ price: total, details: d });
  }, [dirty, total, baseValue, supps, source, hourly, rate, hours]); // eslint-disable-line react-hooks/exhaustive-deps

  const last = proposal?.last;
  const lastValue = last ? (hourly ? last.hourly_rate : last.base) : null;
  const utiliser = () => {
    touch();
    if (hourly) setRate(String(lastValue)); else setBase(String(lastValue));
    setSource({ match: last.match, ride_id: last.ride_id, date: last.date || "" });
  };
  const dejaUtilise = last && num(hourly ? rate : base) === num(lastValue);
  const majorationTouchee = supps.find((s) => MAJORATIONS.includes(s.key) && s.touched);
  const autreProposition = proposal?.supplements[0];
  const signaler = majorationTouchee && autreProposition
    && (autreProposition.key !== majorationTouchee.key || num(autreProposition.amount) !== num(majorationTouchee.amount));
  const aAjouter = !nouvelle && !dirty && autreProposition && !supps.some((s) => MAJORATIONS.includes(s.key));
  const ajouter = () => { touch(); setSupps((list) => [...list, { ...autreProposition, on: true, touched: true }]); };
  const setSupp = (i, patch) => { touch(); setSupps((list) => list.map((s, j) => (j === i ? { ...s, ...patch, touched: true } : s))); };
  const rateTva = proposal?.vat_rate || 0;
  const ht = rateTva ? r2(num(total) / (1 + rateTva)) : num(total);

  return (
    <div className="space-y-3 rounded-xl border border-border p-3 min-w-0" data-testid="ride-pricing">
      <div className="text-xs text-muted-foreground min-h-[1.25rem]" data-testid="ride-price-proposal">
        {!draft.client_id ? "Choisissez le donneur d'ordre : l'outil propose le dernier prix pratiqué."
          : !proposal ? "…"
            : last ? (
              <span className="flex items-start gap-2 flex-wrap">
                <Sparkles className="h-3.5 w-3.5 mt-0.5 shrink-0 text-accent" />
                <span className="flex-1 min-w-0 break-words" data-testid="ride-price-last">
                  {`Prix proposé : ${money(lastValue, cur)}${hourly ? " / h" : ""} — dernier prix du ${dateCourte(last.date, tz)}`}
                  {last.route ? ` · ${last.route}` : ""}{last.vehicle_class ? ` · ${last.vehicle_class}` : ""}
                  {last.route_match === "reverse" ? " (trajet inverse)" : ""}{last.match === "old_total" ? " (prix total d'une ancienne course)" : ""}
                </span>
                {dejaUtilise
                  ? <span className="text-emerald-700 dark:text-emerald-300 font-medium" data-testid="ride-price-used">✓ utilisé</span>
                  : <button type="button" className="text-accent font-medium py-1.5 -my-1.5" onClick={utiliser} disabled={disabled} data-testid="ride-price-use">Utiliser</button>}
              </span>
            ) : `Aucun prix précédent pour ce donneur d'ordre, ce ${hourly ? "type de course" : "trajet"} et cette classe.`}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {hourly ? (
          <div className="space-y-1.5 min-w-0"><Label>Tarif horaire TTC</Label>
            <Input inputMode="decimal" value={rate} onChange={(e) => { touch(); setRate(e.target.value); }} disabled={disabled} data-testid="ride-hourly-rate" />
            <p className="text-xs text-muted-foreground" data-testid="ride-hourly-calc">{`× ${hours || 0} h = ${money(baseValue, cur)}`}</p></div>
        ) : (
          <div className="space-y-1.5 min-w-0"><Label>Prix de base TTC</Label>
            <Input inputMode="decimal" value={base} onChange={(e) => { touch(); setBase(e.target.value); }} disabled={disabled} data-testid="ride-base" /></div>
        )}
      </div>

      {supps.length > 0 && (
        <div className="space-y-2" data-testid="ride-supplements">
          {supps.map((s, i) => (
            <div key={`${s.key}-${i}`} className="flex items-center gap-2 min-w-0" data-testid={`ride-supplement-${s.key}`}>
              <Checkbox checked={s.on} onCheckedChange={(v) => setSupp(i, { on: !!v })} disabled={disabled} data-testid={`ride-supplement-on-${s.key}`} aria-label={s.label} />
              <span className="text-sm flex-1 min-w-0 truncate" title={s.label}>{s.label}</span>
              <Input inputMode="decimal" className="w-24 h-8 shrink-0" value={s.amount} onChange={(e) => setSupp(i, { amount: e.target.value })} disabled={disabled || !s.on}
                data-testid={`ride-supplement-amount-${s.key}`} />
            </div>
          ))}
        </div>
      )}
      {aAjouter && (
        <p className="text-xs text-muted-foreground" data-testid="ride-supplement-offer">
          {`Proposé : ${autreProposition.label} (${money(autreProposition.amount, cur)}). `}
          <button type="button" className="text-accent font-medium py-1.5 -my-1.5" onClick={ajouter} disabled={disabled} data-testid="ride-supplement-add">Ajouter</button>
        </p>
      )}
      {signaler && (
        <p className="text-xs text-amber-700 dark:text-amber-300" data-testid="ride-supplement-hint">
          {`Proposé maintenant : ${autreProposition.label} (${money(autreProposition.amount, cur)}). Votre choix est gardé.`}
        </p>
      )}

      <div className="space-y-1.5"><Label>Prix TTC (€)</Label>
        <Input type="number" step="0.01" value={total} onChange={(e) => { touch(); setManual(true); setTotal(e.target.value); }} data-testid="ride-price" />
        {manual && r2(num(total)) !== calcule && (
          <p className="text-xs text-amber-700 dark:text-amber-300" data-testid="ride-price-manual">
            {`Total modifié à la main (calcul : ${money(calcule, cur)}). `}
            <button type="button" className="underline" onClick={() => { touch(); setManual(false); }} data-testid="ride-price-recalc">Recalculer</button>
          </p>
        )}
        {/* Place toujours réservée (une ligne) : la réponse du serveur n'arrive qu'après l'ouverture, et le contenu de la
            fenêtre ne doit pas bouger sous la souris (bouton « Enregistrer », test 19). */}
        <p className="text-xs text-muted-foreground min-h-4" data-testid="ride-price-vat">
          {!proposal ? " " : proposal.vat_applicable
            ? `HT ${money(ht, cur)} · TVA ${Math.round(rateTva * 1000) / 10} % ${money(r2(num(total) - ht), cur)}`
            : "TVA non applicable (franchise)"}
        </p>
      </div>
    </div>
  );
}
